import { API_ENDPOINT } from "./api";

/**
 * Handles user registration. Creates an unverified account (no password yet)
 * and triggers a 6-digit verification code to be emailed to the user.
 * The password is set later, after the email has been verified.
 * @param {string} email - User's email address
 * @param {Object} [options] - Extra signup data
 * @param {string} [options.role] - "teacher" or "learner"
 * @param {string} [options.teacher_code] - Beta sign-up code (teachers only)
 * @param {string} [options.referrer] - Optional referrer code or source
 * @param {string} [options.signup_button_source] - Optional signup button source
 * @param {string} [options.language] - UI language for the verification email
 *
 * @returns {Promise} - API response with registration result
 */

export const registerUser = async (email, options = {}) => {
    const { role, teacher_code, referrer, signup_button_source, language } = options;
    try {
        const response = await fetch(`${API_ENDPOINT}/auth/register/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                email,
                ...(role && { role }),
                ...(teacher_code && { teacher_code }),
                ...(referrer && { referrer }),
                ...(signup_button_source && { signup_button_source }),
                ...(language && { language }),
            }),
        });

        if (!response.ok) {
            const errorData = await response.json();

            // Handle field-specific validation errors
            if (typeof errorData === "object" && Object.keys(errorData).length > 0) {
                const error = new Error("Validation failed");
                error.fieldErrors = errorData;
                throw error;
            }

            throw new Error(errorData.message || "Registration failed");
        }

        return await response.json();
    } catch (error) {
        // Silently propagate error without logging to console
        throw error;
    }
};

/**
 * Confirms a signup with the 6-digit code. On success the backend returns JWT
 * tokens so the user is logged straight in.
 * @param {string} email - User's email address
 * @param {string} code - The 6-digit verification code
 *
 * @returns {Promise<{access: string, refresh: string}>} - Auth tokens
 */
export const verifyEmailCode = async (email, code) => {
    const response = await fetch(`${API_ENDPOINT}/auth/verify-email/`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, code }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        const error = new Error(data.error || "verification_failed");
        error.code = data.error;
        error.attemptsRemaining = data.attempts_remaining;
        error.status = response.status;
        throw error;
    }

    return data;
};

/**
 * Requests a fresh verification code for an unverified account.
 * Always resolves (the backend never reveals whether the email exists).
 * @param {string} email - User's email address
 * @param {string} [language] - UI language for the email
 *
 * @returns {Promise} - API response
 */
export const resendVerificationCode = async (email, language) => {
    const response = await fetch(`${API_ENDPOINT}/auth/resend-code/`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, ...(language && { language }) }),
    });

    if (!response.ok) {
        throw new Error("Failed to resend code");
    }

    return await response.json();
};

/**
 * Handles user login by sending email and password to the backend
 * @param {string} email - User's email address
 * @param {string} password - User's password
 *
 * @returns {Promise} - API response containing access and refresh tokens
 */
export const loginUser = async (email, password) => {
    try {
        const response = await fetch(`${API_ENDPOINT}/auth/login/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                email,
                password,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json();

            // Handle field-specific validation errors
            if (typeof errorData === "object" && Object.keys(errorData).length > 0) {
                const error = new Error(errorData.detail || "Login failed");
                error.fieldErrors = errorData;
                throw error;
            }

            throw new Error(errorData.detail || "Login failed");
        }

        const data = await response.json();
        // storeTokens(data.access, data.refresh); this is happening within auth
        return data;
    } catch (error) {
        // Silently propagate error without logging to console
        throw error;
    }
};

/**
 * Requests a password reset email for the given address
 * @param {string} email - User's email address
 *
 * @returns {Promise} - API response (always generic, does not reveal if the email exists)
 */
export const forgotPassword = async (email) => {
    const response = await fetch(`${API_ENDPOINT}/auth/forgot-password/`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
    });

    if (!response.ok) {
        const errorData = await response.json();

        if (typeof errorData === "object" && Object.keys(errorData).length > 0) {
            const error = new Error("Request failed");
            error.fieldErrors = errorData;
            throw error;
        }

        throw new Error("Request failed");
    }

    return await response.json();
};

/**
 * Sets a new password using the uid and token from the reset email link
 * @param {string} uid - Base64-encoded user id from the reset link
 * @param {string} token - Reset token from the reset link
 * @param {string} newPassword - The new password to set
 *
 * @returns {Promise} - API response with reset result
 */
export const resetPassword = async (uid, token, newPassword) => {
    const response = await fetch(`${API_ENDPOINT}/auth/reset-password/`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ uid, token, new_password: newPassword }),
    });

    if (!response.ok) {
        const errorData = await response.json();

        if (typeof errorData === "object" && Object.keys(errorData).length > 0) {
            const error = new Error(errorData.error || "Request failed");
            error.fieldErrors = errorData;
            throw error;
        }

        throw new Error("Request failed");
    }

    return await response.json();
};

/**
 * Sets the initial password for the freshly verified (and now logged-in) user.
 * Requires the access token obtained from verifyEmailCode().
 * @param {string} newPassword - The password to set
 *
 * @returns {Promise} - API response
 */
export const setInitialPassword = async (newPassword) => {
    const response = await authenticatedFetch(`${API_ENDPOINT}/auth/set-password/`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ new_password: newPassword }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        const error = new Error(data.error || "Failed to set password");
        error.fieldErrors = data;
        throw error;
    }

    return data;
};

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";

export const storeTokens = (access, refresh) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, access);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
};

export const getAccessToken = () => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
};

export const getRefreshToken = () => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const clearTokens = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
};

/**
 * Refreshes the access token using the refresh token
 * @returns {Promise<string>} - New access token
 */
export const refreshAccessToken = async () => {
    try {
        const refreshToken = getRefreshToken();

        if (!refreshToken) {
            throw new Error("No refresh token available");
        }

        const response = await fetch(`${API_ENDPOINT}/api/token/refresh/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                refresh: refreshToken,
            }),
        });

        if (!response.ok) {
            // If refresh fails, clear tokens and force re-login
            clearTokens();
            throw new Error("Token refresh failed");
        }

        const data = await response.json();
        // Store the new access token (refresh token stays the same)
        localStorage.setItem(ACCESS_TOKEN_KEY, data.access);
        return data.access;
    } catch (error) {
        clearTokens();
        throw error;
    }
};

/**
 * Makes an authenticated fetch request with automatic token refresh on 401
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export const authenticatedFetch = async (url, options = {}) => {
    const accessToken = getAccessToken();

    // Add authorization header if token exists
    const headers = {
        ...options.headers,
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    };

    // Make the initial request
    let response = await fetch(url, { ...options, headers });

    // If we get a 401 and have a refresh token, try to refresh and retry
    if (response.status === 401 && getRefreshToken()) {
        try {
            const newAccessToken = await refreshAccessToken();

            // Retry the request with the new token
            const newHeaders = {
                ...options.headers,
                Authorization: `Bearer ${newAccessToken}`,
            };

            response = await fetch(url, { ...options, headers: newHeaders });
        } catch (refreshError) {
            // If refresh fails, throw the original 401 error
            throw new Error("Authentication expired. Please log in again.");
        }
    }

    return response;
};

/**
 * Fetches the current user's profile (id, email, is_teacher, ...)
 * @returns {Promise<Object>} - user info object
 */
export const getUserInfo = async () => {
    const response = await authenticatedFetch(`${API_ENDPOINT}/auth/user-info/`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        throw new Error("Failed to fetch user info");
    }

    return await response.json();
};
